import { useEffect, useState } from "react";

import useSettings from "../../data/useSettings";
import useCodSignature from "../../data/useCodSignature";
import useDefaultCRUDHandlers from "../../hooks/useDefaultCRUDHandlers";
import { usePageNameContext } from "../../contexts/PageTitleContext";
import AdminSettingsTable from "../../components/adminSettings/AdminSettingsTable";
import FileUpload from "../../components/shared/Inputs/FileUpload";
import { ButtonInput } from "../../components/shared/Inputs/ButtonInput";
import LoadingWheel from "../../components/shared/LoadingWheel";

export default function CodSettings() {
  const { setInfo } = usePageNameContext();
  const { generalSettings, loading: settingsLoading } = useSettings();
  const {
    status,
    loading: signatureLoading,
    upload,
    setApplySignature,
    remove,
  } = useCodSignature();
  const { saveHandlers, deleteHandlers } = useDefaultCRUDHandlers("COD signature");

  const [toUpload, setToUpload] = useState<File[]>([]);

  useEffect(() => {
    setInfo({
      name: "COD",
      desc: "Manage COD credentials and certificate signature",
    });
  }, []);

  // The COD credentials (Username, Password, ...) are the same settings shown on the
  // Admin Settings page - reuse the exact same table so they behave identically.
  const codSettings = (generalSettings ?? []).filter((s) =>
    s.key.startsWith("COD:")
  );

  const handleUpload = () => {
    if (!toUpload.length) return;

    const formData = new FormData();
    formData.append("signature", toUpload[0]);

    upload.mutate(formData, {
      onSuccess: () => {
        setToUpload([]);
        saveHandlers.onSuccess();
      },
      onError: saveHandlers.onError,
    });
  };

  const handleToggle = (value: boolean) => {
    setApplySignature.mutate(value, saveHandlers);
  };

  const handleRemove = () => {
    remove.mutate(undefined, deleteHandlers);
  };

  return (
    <div className="space-y-8">
      {/* Credentials */}
      <section className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold text-gray-900">COD Credentials</h2>
        <p className="mt-1 text-sm text-gray-500">
          Username and password used to connect to the Certificate of Destruction service.
        </p>

        {settingsLoading ? (
          <div className="mt-4">
            <LoadingWheel />
          </div>
        ) : codSettings.length ? (
          <AdminSettingsTable settings={codSettings} />
        ) : (
          <p className="mt-4 text-sm text-gray-500">No COD credentials found.</p>
        )}
      </section>

      {/* Signature */}
      <section className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold text-gray-900">COD Signature</h2>
        <p className="mt-1 text-sm text-gray-500">
          Upload a signature (PNG). When enabled, it is added to the Holder/Owner and
          Issuing Establishment signature lines on every generated COD certificate.
        </p>

        {signatureLoading ? (
          <div className="mt-4">
            <LoadingWheel />
          </div>
        ) : (
          <>
            {/* Apply toggle */}
            <div className="mt-4 flex items-center">
              <input
                type="checkbox"
                id="cod-apply-signature"
                checked={status?.applySignature ?? false}
                disabled={!status?.signatureUploaded || setApplySignature.isLoading}
                onChange={(e) => handleToggle(e.target.checked)}
                className="h-5 w-5 rounded border-gray-300 text-primary-600 focus:ring-primary-500 disabled:opacity-50"
              />
              <label
                htmlFor="cod-apply-signature"
                className="ml-2 block text-sm font-medium text-gray-700"
              >
                Apply signature to COD certificates
              </label>
            </div>
            {!status?.signatureUploaded && (
              <p className="mt-1 text-xs text-gray-400">
                Upload a signature below to enable this.
              </p>
            )}

            {/* Current signature */}
            {status?.signatureUploaded && status?.signatureBase64 && (
              <div className="mt-6">
                <span className="text-sm font-medium text-gray-700">
                  Current signature
                </span>
                <div className="mt-2 flex items-center gap-4">
                  <img
                    src={`data:image/png;base64,${status.signatureBase64}`}
                    alt="COD signature"
                    className="h-24 max-w-xs object-contain border border-gray-200 rounded bg-gray-50 p-2"
                  />
                  <ButtonInput
                    label="Remove"
                    isSubmit={false}
                    onClick={handleRemove}
                    isLoading={remove.isLoading}
                    classes="bg-red-600 hover:bg-red-700"
                  />
                </div>
              </div>
            )}

            {/* Uploader */}
            <div className="mt-6">
              <FileUpload
                label={status?.signatureUploaded ? "Replace Signature" : "Upload Signature"}
                accept=".png,.jpg,.jpeg"
                desc="Transparent PNG recommended"
                updateFilesCb={(files: File[]) => setToUpload(files)}
              />
              <ButtonInput
                label="Upload"
                isSubmit={false}
                onClick={handleUpload}
                disabled={!toUpload.length}
                isLoading={upload.isLoading}
                classes={undefined}
              />
            </div>
          </>
        )}
      </section>
    </div>
  );
}
